import { Input } from "@getgreenline/blaze-ui";
import { cn } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { pacingOptions, pricingOptions } from "@/app-config";
import { useMarketsCatalog } from "@/hooks/use-markets-catalog";
import { getBudgetSummary } from "@/utils/budget";
import { type CampaignFormValues } from "../campaign-form";
import { BudgetPacingCard } from "../cards/budget-pacing-card";
import { BudgetPricingCard } from "../cards/budget-pricing-card";
import { BudgetDateInput } from "../../shared/budget-date-input";
import { CampaignContextPill } from "../../shared/campaign-context-pill";
import { DailyDistributionCard } from "../cards/daily-distribution-card";
import { FormFieldLabel } from "../../shared/form-field-label";
import { FormTip } from "../../shared/form-tip";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type BudgetStepProps = {
  errorMessage?: string;
  errorField?: string;
  showError?: boolean;
  onDismissError: () => void;
};

export function BudgetStep({
  errorMessage,
  errorField,
  showError = false,
  onDismissError,
}: BudgetStepProps) {
  const { values, handleChange, setFieldValue } =
    useFormikContext<CampaignFormValues>();
  const selectedBrand = values.brand;
  const { data: states = [] } = useMarketsCatalog(selectedBrand?.country);
  const selectedState = states.find(
    (state) => state.code === values.marketState,
  );
  const { formattedBudget, campaignDays, dailyBudget } =
    getBudgetSummary(values);

  return (
    <section className="tw:max-w-5xl">
      <StepHeader
        title="Budget"
        description="Set your total campaign budget, flight dates, and pacing preference. No manual bid management needed."
      />

      <CampaignContextPill
        brandName={selectedBrand?.name}
        details={[selectedState?.name]}
      />

      <StepAlert
        show={showError}
        message={errorMessage}
        onDismiss={onDismissError}
      />

      <div className="tw:mt-6">
        <FormFieldLabel className="tw:mb-0">Pricing model</FormFieldLabel>
        <div
          className={cn(
            "tw:mt-4 tw:grid tw:grid-cols-2 tw:gap-4",
            showError &&
              errorField === "pricingModel" &&
              "tw:rounded-xl tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3",
          )}
        >
          {pricingOptions.map((option) => (
            <BudgetPricingCard
              key={option.id}
              option={option}
              selected={values.pricingModel === option.id}
              onSelect={() => setFieldValue("pricingModel", option.id)}
            />
          ))}
        </div>
      </div>

      <div className="tw:mt-6">
        <FormFieldLabel className="tw:mb-0">Campaign schedule</FormFieldLabel>
        <div className="tw:mt-3 tw:grid tw:grid-cols-2 tw:gap-4">
          <BudgetDateInput
            label="Start date"
            name="budgetStartDate"
            value={values.budgetStartDate}
            max={values.budgetEndDate || undefined}
            invalid={showError && errorField === "budgetStartDate"}
            onChange={handleChange}
          />
          <BudgetDateInput
            label="End date (optional)"
            name="budgetEndDate"
            value={values.budgetEndDate}
            min={values.budgetStartDate || undefined}
            invalid={showError && errorField === "budgetEndDate"}
            onChange={(event) => {
              const nextEndDate = event.target.value;
              handleChange(event);

              if (!nextEndDate && values.pacing === "even") {
                setFieldValue("pacing", "none");
              } else if (!values.budgetEndDate) {
                setFieldValue("pacing", "even");
              }
            }}
          />
        </div>
      </div>

      <div className="tw:mt-6">
        <FormFieldLabel className="tw:mb-0">
          Total campaign budget
        </FormFieldLabel>
        <div className="tw:mt-3 tw:max-w-xl">
          <div
            className={cn(
              "tw:flex tw:items-end tw:gap-3 tw:border-b-2 tw:border-sky-400 tw:pb-2",
              showError &&
                errorField === "totalBudget" &&
                "tw:border-destructive",
            )}
          >
            <span className="tw:text-2xl tw:text-muted-foreground">$</span>
            <Input
              name="totalBudget"
              value={values.totalBudget}
              onChange={(event) => {
                const nextValue = event.target.value.replace(/[^0-9]/g, "");
                setFieldValue("totalBudget", nextValue);
              }}
              placeholder="3600"
              aria-label="Total campaign budget"
              aria-invalid={showError && errorField === "totalBudget"}
              className="tw:h-auto tw:border-0 tw:px-0 tw:py-0 tw:text-3xl! tw:font-semibold tw:leading-none tw:shadow-none tw:placeholder:text-muted-foreground/50 focus-visible:tw:ring-0"
            />
            <span className="tw:pb-1 tw:text-sm tw:font-medium tw:text-muted-foreground">
              total
            </span>
          </div>
          <FormTip className="tw:mt-2">Minimum $100 total</FormTip>
        </div>
      </div>

      <div className="tw:mt-6">
        <FormFieldLabel className="tw:mb-0">Pacing</FormFieldLabel>
        <div
          className={cn(
            "tw:mt-4 tw:grid tw:grid-cols-2 tw:gap-4",
            showError &&
              errorField === "pacing" &&
              "tw:rounded-xl tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3",
          )}
        >
          {pacingOptions
            .filter((option) => values.budgetEndDate || option.id !== "even")
            .map((option) => (
              <BudgetPacingCard
                key={option.id}
                option={option}
                selected={values.pacing === option.id}
                onSelect={() => setFieldValue("pacing", option.id)}
              />
            ))}
        </div>
      </div>

      {values.pacing === "even" ? (
        <DailyDistributionCard
          dailyBudget={dailyBudget}
          formattedBudget={formattedBudget}
          campaignDays={campaignDays}
        />
      ) : values.pacing === "custom" ? (
        <div className="tw:mt-6 tw:max-w-xl">
          <FormFieldLabel className="tw:mb-0">
            Daily pacing cap <span className="tw:text-destructive">*</span>
          </FormFieldLabel>
          <div
            className={cn(
              "tw:mt-3 tw:flex tw:items-end tw:gap-3 tw:border-b-2 tw:border-sky-400 tw:pb-2",
              showError &&
                errorField === "dailyPacing" &&
                "tw:border-destructive",
            )}
          >
            <span className="tw:text-2xl tw:text-muted-foreground">$</span>
            <Input
              name="dailyPacing"
              value={values.dailyPacing}
              onChange={(event) => {
                const nextValue = event.target.value.replace(/[^0-9]/g, "");
                setFieldValue("dailyPacing", nextValue);
              }}
              placeholder="100"
              aria-label="Daily pacing cap"
              aria-invalid={showError && errorField === "dailyPacing"}
              className="tw:h-auto tw:border-0 tw:px-0 tw:py-0 tw:text-3xl! tw:font-semibold tw:leading-none tw:shadow-none tw:placeholder:text-muted-foreground/50 focus-visible:tw:ring-0"
            />
            <span className="tw:pb-1 tw:text-sm tw:font-medium tw:text-muted-foreground">
              per day
            </span>
          </div>
          <FormTip className="tw:mt-2">
            Set the maximum amount this campaign can spend each day.
          </FormTip>
        </div>
      ) : null}
    </section>
  );
}
